import { useRef, useEffect } from 'react';
import { TEMPLATE_SIZE, LOGO_BOUNDS, TEMPLATE_BOUNDS, getAdaptiveFontSize, DEFAULT_LOGO_SIZE } from '../../../shared/lib/video';
import { exportTemplateToBlob } from '../../../shared/lib/canvas-export';
import { buildIntroExportOptions } from '../lib/buildIntroExportOptions';
import { applyFontVariationToCanvas } from '../../../shared/lib/variableFontUtils';

interface VideoProcessorProps {
  videoFile: File;
  logoFile?: File | null;
  introImageFile?: File | null;
  overlayText: string;
  textPosition: { x: number; y: number; previewWidth: number; previewHeight: number; textBoxWidth: number; bottom?: number; lines?: string[] };
  backgroundColor?: string;
  reelBackgroundUrl?: string;
  textColor?: string;
  useTwoColors?: boolean;
  textColorSecondary?: string;
  fontFamily?: string;
  fontUrl?: string;
  textUppercase?: boolean;
  fontVariationSettings?: Record<string, number>;
  logoPosition?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-right';
  logoSize?: number;
  lineSpacing?: number;
  textDuration?: number | 'full';
  fontSize?: number | null;
  textBoxStyle?: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
  textBoxAngle?: number;
  textBoxRadius?: number;
  textBoxPadding?: number;
  textBoxPaddingTop?: number;
  textBoxPaddingLeft?: number;
  textBoxPaddingRight?: number;
  textAlign?: 'left' | 'center' | 'right';
  hCenterX?: number;
  onProgress: (progress: number) => void;
  onComplete: (blob: Blob) => void;
  onError: (error: string) => void;
}

export function VideoProcessor({
  videoFile, logoFile, introImageFile, overlayText, textPosition,
  backgroundColor, reelBackgroundUrl, textColor, useTwoColors, textColorSecondary,
  fontFamily, fontUrl, textUppercase, fontVariationSettings,
  logoPosition, logoSize, lineSpacing, textDuration = 'full', fontSize = null,
  textBoxStyle, textBoxAngle, textBoxRadius, textBoxPadding, textBoxPaddingTop, textBoxPaddingLeft, textBoxPaddingRight,
  textAlign, hCenterX,
  onProgress, onComplete, onError,
}: VideoProcessorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (videoFile && !processingRef.current) {
      processingRef.current = true;
      processVideo();
    }
  }, [videoFile, logoFile, introImageFile, overlayText, textPosition]);

  async function processVideo() {
    let videoUrl = '';
    let audioCtx: AudioContext | null = null;

    try {
      // 1. Загружаем ресурсы
      const video = document.createElement('video');
      videoUrl = URL.createObjectURL(videoFile);
      video.src = videoUrl;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      video.preload = 'auto';

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Failed to load video'));
      });

      // Ждём буферизации
      if (video.readyState < 3) {
        await new Promise<void>((resolve) => {
          video.oncanplay = () => resolve();
        });
      }

      const logoImg = logoFile ? await loadImageFromFile(logoFile) : null;
      const introImg = introImageFile ? await loadImageFromFile(introImageFile) : null;
      // Фон рилса (URL шаблона) — рисуется позади видео в полях letterbox.
      // Грузим best-effort: при ошибке остаёмся на чёрной заливке (бэк-совместимо).
      const bgImg = reelBackgroundUrl ? await loadImageFromUrl(reelBackgroundUrl).catch(() => null) : null;

      const canvas = canvasRef.current;
      if (!canvas) throw new Error('Canvas not found');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      canvas.width = TEMPLATE_SIZE.width;
      canvas.height = TEMPLATE_SIZE.height;

      const fps = 30;
      const duration = video.duration;

      // 2. Настраиваем аудио через Web Audio API
      audioCtx = new AudioContext();
      const audioSource = audioCtx.createMediaElementSource(video);
      const audioDest = audioCtx.createMediaStreamDestination();
      audioSource.connect(audioDest);
      // НЕ подключаем к динамикам — тихая обработка

      // 3. Создаём поток: canvas (видео) + Web Audio (аудио)
      const canvasStream = canvas.captureStream(fps);
      const audioTracks = audioDest.stream.getAudioTracks();
      if (audioTracks.length > 0) {
        canvasStream.addTrack(audioTracks[0]);
        console.log('Audio track added to stream');
      } else {
        console.warn('No audio tracks available');
      }

      // 4. MediaRecorder — выбираем лучший формат
      const mimeType = chooseMimeType();
      console.log('Using mimeType:', mimeType);

      const recorder = new MediaRecorder(canvasStream, {
        mimeType,
        videoBitsPerSecond: 4_000_000,
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      // 5. Intro frame (если есть)
      const hasIntro = !!overlayText && !!introImg;
      
      if (hasIntro) {
        console.log('Generating intro frame...');
        await generateIntroFrame(canvas, ctx, introImg, logoImg);
        
        // Начинаем запись, держим intro кадр
        recorder.start(100);
        await delay(Math.ceil(1000 / fps) + 10); // 1 кадр + небольшой запас
      } else {
        recorder.start(100);
      }

      // 6. Воспроизводим видео и рисуем на canvas в реальном времени
      video.currentTime = 0;
      video.muted = false; // Нужно для захвата аудио через MediaElementSource
      
      // Пробуем play — если не получится unmuted, мутим и пробуем снова
      try {
        await video.play();
      } catch {
        console.warn('Unmuted play failed, trying muted...');
        video.muted = true;
        await video.play();
      }

      // Рисуем каждый кадр
      await new Promise<void>((resolve) => {
        let lastProgress = 0;

        const draw = () => {
          if (video.ended) {
            resolve();
            return;
          }

          // Рисуем видео на canvas
          drawVideoFrame(video, canvas, ctx, bgImg);

          // Рисуем оверлеи (лого + текст)
          drawOverlays(ctx, canvas, video.currentTime, duration, logoImg);

          // Прогресс
          const progress = Math.min((video.currentTime / duration) * 100, 100);
          if (progress - lastProgress > 0.5) {
            onProgress(progress);
            lastProgress = progress;
          }

          requestAnimationFrame(draw);
        };

        video.onended = () => {
          // Финальный кадр
          drawVideoFrame(video, canvas, ctx, bgImg);
          drawOverlays(ctx, canvas, duration, duration, logoImg);
          onProgress(100);
          resolve();
        };

        requestAnimationFrame(draw);
      });

      // 7. Останавливаем запись
      recorder.stop();
      await new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
      });

      // 8. Собираем результат
      const blob = new Blob(chunks, { type: mimeType });
      console.log(`Processing complete! Output: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
      
      onComplete(blob);
      processingRef.current = false;

    } catch (error) {
      console.error('Processing error:', error);
      onError(error instanceof Error ? error.message : 'Unknown error');
      processingRef.current = false;
    } finally {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (audioCtx) audioCtx.close().catch(() => {});
    }
  }

  // --- Отрисовка кадра видео на canvas ---
  function drawVideoFrame(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    bgImg: HTMLImageElement | null,
  ) {
    const videoAspect = video.videoWidth / video.videoHeight;
    const canvasAspect = canvas.width / canvas.height;

    let drawWidth: number, drawHeight: number, offsetX: number, offsetY: number;

    if (videoAspect > canvasAspect) {
      drawWidth = canvas.width;
      drawHeight = drawWidth / videoAspect;
      offsetX = 0;
      offsetY = (canvas.height - drawHeight) / 2;
    } else {
      drawHeight = canvas.height;
      drawWidth = drawHeight * videoAspect;
      offsetX = (canvas.width - drawWidth) / 2;
      offsetY = 0;
    }

    if (bgImg) {
      ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
    } else if (drawWidth < canvas.width || drawHeight < canvas.height) {
      // Фон не задан, а видео не закрывает кадр (горизонтальное) — вместо
      // чёрных полос подкладываем размытый кадр самого видео (cover).
      // Медиа: «казакстан24 артқы фон жок горизонтальный видеога».
      const coverScale = Math.max(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
      const cw = video.videoWidth * coverScale;
      const chh = video.videoHeight * coverScale;
      ctx.save();
      ctx.filter = 'blur(28px)';
      ctx.drawImage(video, (canvas.width - cw) / 2, (canvas.height - chh) / 2, cw, chh);
      ctx.restore();
      // Полупрозрачное затемнение, чтобы блюр не спорил с текстом.
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);
  }

  // --- Отрисовка оверлеев (лого + текст) ---
  function drawOverlays(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    currentTime: number,
    duration: number,
    logoImg: HTMLImageElement | null,
  ) {
    // Лого
    if (logoImg) {
      const size = logoSize && logoSize > 0 ? logoSize : DEFAULT_LOGO_SIZE;
      ctx.drawImage(logoImg, LOGO_BOUNDS.x, LOGO_BOUNDS.y, size, size);
    }

    // Текст
    const shouldDrawText = textDuration === 'full' || currentTime <= (textDuration as number);
    if (!shouldDrawText || !overlayText) return;

    const scaleX = TEMPLATE_SIZE.width / textPosition.previewWidth;
    const scaleY = TEMPLATE_SIZE.height / textPosition.previewHeight;
    const textX = textPosition.x * scaleX;
    const padTop = (textBoxPaddingTop ?? textBoxPadding ?? 12) * scaleX;
    const padLeft = (textBoxPaddingLeft ?? textBoxPadding ?? 12) * scaleX;
    const padRight = (textBoxPaddingRight ?? textBoxPadding ?? 12) * scaleX;
    const fontSizePreview = (fontSize ?? getAdaptiveFontSize(overlayText.length));
    const scaledFontSize = fontSizePreview * scaleX;
    const lineHeight = scaledFontSize * (lineSpacing && lineSpacing > 0 ? lineSpacing : 1.0);
    const displayText = textUppercase ? overlayText.toUpperCase() : overlayText;

    applyFontVariationToCanvas(ctx, scaledFontSize, fontFamily || 'Arial', fontVariationSettings);

    const FULL_WIDTH = TEMPLATE_BOUNDS.right - TEMPLATE_BOUNDS.left;
    const NOTCH_WIDTH = TEMPLATE_BOUNDS.rightCornerLeft - TEMPLATE_BOUNDS.left;

    const wrapLines = (availableWidth: number): string[] => {
      const words = displayText.split(' ');
      const out: string[] = [];
      let line = '';
      const maxLineWidth = availableWidth - padLeft - padRight;
      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + ' ';
        if (ctx.measureText(testLine).width > maxLineWidth && i > 0) {
          out.push(line.trim());
          line = words[i] + ' ';
        } else {
          line = testLine;
        }
      }
      if (line) out.push(line.trim());
      return out;
    };
    const boxHeightFor = (lineCount: number) => lineCount * lineHeight + padTop * 2;

    const bottom = textPosition.bottom;
    let lines: string[];
    let textY: number;

    if (textPosition.lines) {
      lines = textPosition.lines;
      textY =
        typeof bottom === 'number'
          ? TEMPLATE_SIZE.height - bottom - boxHeightFor(lines.length)
          : textPosition.y * scaleY;
    } else if (typeof bottom === 'number') {
      const bottomEdgeY = TEMPLATE_SIZE.height - bottom;
      let availableWidth = FULL_WIDTH;
      lines = wrapLines(availableWidth);
      textY = bottomEdgeY - boxHeightFor(lines.length);
      const refined = textY < TEMPLATE_BOUNDS.rightCornerTop ? FULL_WIDTH : NOTCH_WIDTH;
      if (refined !== availableWidth) {
        availableWidth = refined;
        lines = wrapLines(availableWidth);
        textY = bottomEdgeY - boxHeightFor(lines.length);
      }
    } else {
      textY = textPosition.y * scaleY;
      const availableWidth = textY < TEMPLATE_BOUNDS.rightCornerTop ? FULL_WIDTH : NOTCH_WIDTH;
      lines = wrapLines(availableWidth);
    }

    // Размеры текстового блока
    let actualTextBoxWidth = 0;
    for (const textLine of lines) {
      actualTextBoxWidth = Math.max(actualTextBoxWidth, ctx.measureText(textLine).width);
    }
    const textBoxWidth = actualTextBoxWidth + padLeft + padRight;
    const textBoxHeight = boxHeightFor(lines.length);

    // Пресет фон-бокса. square-flush: бокс УДЛИНЯЕТСЯ влево до края (x=0).
    // square-flush-right: УДЛИНЯЕТСЯ вправо до края (TEMPLATE_SIZE.width). В обоих
    // текст на месте. острые углы для всех square.
    const boxStyle = textBoxStyle ?? 'rounded';
    const hCenter = hCenterX != null;
    const isFlush = !hCenter && boxStyle === 'square-flush';
    const isFlushRight = !hCenter && boxStyle === 'square-flush-right';
    const centeredLeft = hCenter
      ? Math.max(TEMPLATE_BOUNDS.left, hCenterX! * TEMPLATE_SIZE.width - textBoxWidth / 2)
      : 0;
    const boxDrawX = hCenter ? centeredLeft : isFlush ? 0 : textX;
    const boxDrawWidth = hCenter
      ? textBoxWidth
      : isFlush
        ? textX + textBoxWidth
        : isFlushRight
          ? TEMPLATE_SIZE.width - textX
          : textBoxWidth;

    const align = textAlign ?? 'left';
    const baseLeft = hCenter ? boxDrawX : textX;
    const lineX = (line: string): number => {
      const base = baseLeft + padLeft;
      if (align === 'left') return base;
      const w = ctx.measureText(line).width;
      return align === 'center' ? base + (actualTextBoxWidth - w) / 2 : base + (actualTextBoxWidth - w);
    };

    // Наклон всей плашки (бокс + текст) вокруг центра. rotate(0) = identity.
    const boxAngle = ((textBoxAngle ?? 0) * Math.PI) / 180;
    const boxCx = boxDrawX + boxDrawWidth / 2;
    const boxCy = textY + textBoxHeight / 2;
    ctx.save();
    ctx.translate(boxCx, boxCy);
    ctx.rotate(boxAngle);
    ctx.translate(-boxCx, -boxCy);

    // Фон
    ctx.fillStyle = backgroundColor || 'black';
    // Радиус = % от половины высоты бокса (0..100). Округлость не зависит от отступа.
    // Дефолт: rounded→30%, flush/square→0. 100% = капсула.
    const defaultRadiusPct = boxStyle === 'rounded' ? 30 : 0;
    const radiusPct = Math.max(0, Math.min(100, textBoxRadius ?? defaultRadiusPct));
    const cr = (radiusPct / 100) * (textBoxHeight / 2);
    // flush упирается краем в кадр — тот край острый, скругляем только внутренний.
    // roundRect: [tl, tr, br, bl]. flush-left → правые; flush-right → левые.
    const radii = isFlush ? [0, cr, cr, 0] : isFlushRight ? [cr, 0, 0, cr] : cr;
    ctx.beginPath();
    ctx.roundRect(boxDrawX, textY, boxDrawWidth, textBoxHeight, radii);
    ctx.fill();

    // Текст
    ctx.textBaseline = 'top';
    let y = textY + padTop;
    for (let i = 0; i < lines.length; i++) {
      if (useTwoColors && textColorSecondary) {
        ctx.fillStyle = i === 0 ? (textColor || 'white') : textColorSecondary;
      } else {
        ctx.fillStyle = textColor || 'white';
      }
      ctx.fillText(lines[i], lineX(lines[i]), y);
      y += lineHeight;
    }
    ctx.restore();
  }

  // --- Intro frame ---
  async function generateIntroFrame(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    introImg: HTMLImageElement,
    logoImg: HTMLImageElement | null,
  ) {
    const introImageUrl = URL.createObjectURL(await fetch(introImg.src).then(r => r.blob()));
    const logoUrl = logoImg ? URL.createObjectURL(await fetch(logoImg.src).then(r => r.blob())) : undefined;

    const introBlob = await exportTemplateToBlob(buildIntroExportOptions({
      backgroundColor: backgroundColor || '#000000',
      textColor: textColor || '#ffffff',
      useTwoColors,
      textColorSecondary,
      fontFamily: fontFamily || 'Arial',
      fontUrl,
      logoUrl,
      logoPosition,
      imageUrl: introImageUrl,
      text: overlayText,
      textUppercase,
      fontSize,
      previewWidth: textPosition.previewWidth,
    }));

    const img = new Image();
    const blobUrl = URL.createObjectURL(introBlob);
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = blobUrl;
    });

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const yOffset = (canvas.height - 1350) / 2;
    ctx.drawImage(img, 0, yOffset, 1080, 1350);

    URL.revokeObjectURL(blobUrl);
    URL.revokeObjectURL(introImageUrl);
    if (logoUrl) URL.revokeObjectURL(logoUrl);
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', left: '-9999px', top: '-9999px', pointerEvents: 'none' }}
    />
  );
}

// --- Утилиты ---

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function chooseMimeType(): string {
  const candidates = [
    'video/mp4;codecs=avc1',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return mime;
    }
  }
  return 'video/webm'; // fallback
}
